import { useState } from 'react';
import Modal from '@mui/material/Modal';
import { IoClose as Close } from 'react-icons/io5';
import { IoWarningOutline as Warning } from 'react-icons/io5';
import { HiOutlineShieldExclamation, HiOutlineLockClosed, HiOutlineUserRemove } from 'react-icons/hi';
import { ReactComponent as UsersIcon } from '@Assets/users.svg';
import { ReactComponent as TeamIcon } from '@Assets/team.svg';
import { ReactComponent as DeleteIcon } from '@Assets/delete.svg';
import { ReactComponent as ProfilePicture } from '@Assets/profilePicture.svg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import ErrorHandler from '@Root/ErrorHandler';
import './RemoveCollaboratorForm.scss';

export default function RemoveCollaboratorForm(props) {

    const [cursor, setCursor] = useState('auto');
    const { removeSimpleUserByUserId } = useReduxProject();
    const projectService = new ProjectService();
    const teamService = new TeamService();
    const orgService = new OrgService();
    const errorHandler = new ErrorHandler();
    const className = 'removeCollaboratorForm';

    const handleRemove = async(e) => {
        e.preventDefault();
        setCursor('wait');
        try {
            let body;
            if (props.parentType === 'org') {
                body = {
                    userId: props.user?.id,
                    orgId: props.org?.id,
                    childType: props.childType
                };
                await orgService.removeContributorPermissionFromOrganization(body);
            }
            if (props.parentType === 'team') {
                body = {
                    userId: props.user?.id,
                    teamId: props.team?.id,
                    childType: props.childType
                };
                await teamService.removeContributorPermissionFromTeam(body);
            }
            if (props.parentType === 'project') {
                if(props.childType === 'user') {
                    body = {
                        userId: props.user?.id,
                        projectId: props.project?.id,
                        childType: props.childType
                    };
                }
                if(props.childType === 'team') {
                    body = {
                        teamId: props.team?.id,
                        projectId: props.project?.id,
                        childType: props.childType
                    };
                }
                let removeUser = await projectService.removeContributorPermissionFromProject(body);
                if(removeUser.status <= 200 && removeUser.body === 'true') {
                    if(props.childType === 'user') {
                        removeSimpleUserByUserId(body.userId, body.projectId);
                    }
                }
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
        props.reloadData();
        props.closeAllPopup();
    };

    const dataURLtoFile = (dataurl) => {
        try {
            // Validate that dataurl is a proper data URL
            if (!dataurl || !dataurl.startsWith('data:')) {
                console.error('Invalid data URL:', dataurl);
                return null;
            }

            var arr = dataurl.split(',');
            
            // Check if we have both parts
            if (arr.length !== 2) {
                console.error('Invalid data URL format');
                return null;
            }

            var mime = arr[0].match(/:(.*?);/);
            if (!mime || !mime[1]) {
                console.error('Could not extract MIME type');
                return null;
            }

            var bstr = atob(arr[1]);
            var n = bstr.length;
            var u8arr = new Uint8Array(n);
            
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            
            return new File([u8arr], 'photo.jpg', { type: mime[1] });
        } catch (err) {
            console.error('Error converting data URL to file:', err);
            return null;
        }
    };

    const getTargetName = () => {
        if (props.childType === 'user') {
            return props.user?.fullName || 'User';
        }
        if (props.childType === 'team') {
            return props.team?.teamName || 'Team';
        }
        return '';
    };

    const getTargetAvatar = () => {
        if (props.childType === 'user' && props.user?.photo) {
            return (
                <img 
                    src={URL.createObjectURL(dataURLtoFile(props.user.photo))}
                    alt="Avatar"
                />
            );
        }
        if (props.childType === 'user') {
            return <ProfilePicture />;
        }
        if (props.childType === 'team') {
            return <TeamIcon />;
        }
        return <UsersIcon />;
    };

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            style={{cursor: cursor}}
            className={className}
        >
            <div className={`${className}__inner`}>
                {/* Title Bar */}
                <div className={`${className}__inner__titleBar`}>
                    <div className={`${className}__inner__titleBar__title`}>
                        <div className={`${className}__inner__titleBar__title__tag`}>
                            {props.childType === 'team' ? <TeamIcon /> : <UsersIcon />}
                        </div>
                        <div className={`${className}__inner__titleBar__title__text`}>
                            <div className={`${className}__inner__titleBar__title__label`}>
                                <DeleteIcon />
                                Remove {props.childType}
                            </div>
                            <h2 className={`${className}__inner__titleBar__title__type`}>
                                Remove {props.childType === 'user' ? 'Collaborator' : 'Team'}
                            </h2>
                        </div>
                    </div>
                    <Close 
                        className={`${className}__inner__titleBar__close`} 
                        onClick={props.closePopup}
                    />
                </div>

                {/* Content */}
                <div className={`${className}__inner__content`}>
                    {/* Target User/Team Card */}
                    <div className={`${className}__inner__target`}>
                        <div className={`${className}__inner__target__avatar`}>
                            {getTargetAvatar()}
                        </div>
                        <div className={`${className}__inner__target__info`}>
                            <h3 className={`${className}__inner__target__name`}>
                                {getTargetName()}
                            </h3>
                            <p className={`${className}__inner__target__type`}>
                                {props.childType} • {props.parentType}
                            </p>
                        </div>
                        <div className={`${className}__inner__target__badge`}>
                            To Remove
                        </div>
                    </div>

                    {/* Warning Section */}
                    <div className={`${className}__inner__warning`}>
                        <Warning className={`${className}__inner__warning__icon`} />
                        <div className={`${className}__inner__warning__text`}>
                            <h4 className={`${className}__inner__warning__title`}>
                                This action cannot be undone
                            </h4>
                            <p className={`${className}__inner__warning__description`}>
                                Removing this {props.childType} will revoke all their permissions and remove them from this {props.parentType}. They will lose access immediately.
                            </p>
                        </div>
                    </div>

                    {/* Impact List */}
                    <div className={`${className}__inner__impacts`}>
                        <h4 className={`${className}__inner__impacts__title`}>
                            What will happen:
                        </h4>
                        <div className={`${className}__inner__impacts__list`}>
                            <div className={`${className}__inner__impacts__item`}>
                                <HiOutlineLockClosed className={`${className}__inner__impacts__item__icon`} />
                                All permissions will be revoked immediately
                            </div>
                            <div className={`${className}__inner__impacts__item`}>
                                <HiOutlineUserRemove className={`${className}__inner__impacts__item__icon`} />
                                {props.childType === 'user' ? 'User' : 'Team'} will be removed from the {props.parentType}
                            </div>
                            <div className={`${className}__inner__impacts__item`}>
                                <HiOutlineShieldExclamation className={`${className}__inner__impacts__item__icon`} />
                                Access to all resources will be lost
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className={`${className}__inner__actions`}>
                    <div className={`${className}__inner__actions__cancel`}>
                        <button onClick={props.closePopup}>
                            Cancel
                        </button>
                    </div>
                    <div className={`${className}__inner__actions__remove`}>
                        <button 
                            onClick={handleRemove}
                            disabled={cursor === 'wait'}
                        >
                            {cursor === 'wait' ? 'Removing...' : 'Remove ' + (props.childType === 'user' ? 'User' : 'Team')}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
